import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Target, Plus, Pencil, X, Check, GripVertical, Star } from 'lucide-react';
import { Button } from './Button';
import { LoadingSpinner } from './LoadingSpinner';
import { MostActiveLadder } from './MostActiveLadder';
import { FullLadderView } from './FullLadderView';
import { SectionAccordion } from './SectionAccordion';
import { supabase } from '../lib/supabase';
import type { Goal, LadderWithSteps, MostActiveLadderPath } from '../types/ladder';
import {
  fetchGoalsWithLadders,
  fetchLaddersForGoal,
  getMostActiveLadderPath,
  toggleGoalPriority,
} from '../lib/ladderUtils';

interface PlannerSectionProps {
  userId: string;
  isOpen: boolean;
  onToggle: (key: string) => void;
  sectionKey: string;
}

export function PlannerSection({ userId, isOpen, onToggle, sectionKey }: PlannerSectionProps) {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
  const [activeGoalExpanded, setActiveGoalExpanded] = useState(false);
  const [activeView, setActiveView] = useState<'working' | 'full'>('working');
  const [mostActivePath, setMostActivePath] = useState<MostActiveLadderPath | null>(null);
  const [allLadders, setAllLadders] = useState<LadderWithSteps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [workingViewOpen, setWorkingViewOpen] = useState(false);
  const [fullViewOpen, setFullViewOpen] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
  const [editedName, setEditedName] = useState('');
  const [editedAlignment, setEditedAlignment] = useState('');
  const [editedOutcome, setEditedOutcome] = useState('');
  const [ladderCounts, setLadderCounts] = useState<Record<string, number>>({});
  const [goalRecency, setGoalRecency] = useState<Record<string, 'green' | 'yellow' | 'red' | null>>({});
  const [draggedGoalId, setDraggedGoalId] = useState<string | null>(null);
  const [dragOverGoalId, setDragOverGoalId] = useState<string | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchCurrentY, setTouchCurrentY] = useState<number | null>(null);
  const [touchDraggedGoalId, setTouchDraggedGoalId] = useState<string | null>(null);
  const [coreValues, setCoreValues] = useState<string[]>([]);

  const formatCoreValuesText = (values: string[]): string => {
    if (values.length === 0) return 'your core values';
    if (values.length === 1) return `your value of ${values[0]}`;
    if (values.length === 2) return `your values of ${values[0]} and ${values[1]}`;
    const lastValue = values[values.length - 1];
    const otherValues = values.slice(0, -1).join(', ');
    return `your values of ${otherValues}, and ${lastValue}`;
  };

  const calculateRecency = (lastActivityAt: string | null): 'green' | 'yellow' | 'red' | null => {
    if (!lastActivityAt) return null;

    const now = new Date();
    const lastActivity = new Date(lastActivityAt);
    const daysSinceActivity = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSinceActivity <= 7) return 'green';
    if (daysSinceActivity <= 14) return 'yellow';
    if (daysSinceActivity >= 21) return 'red';
    return null;
  };

  const loadCoreValues = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('core_value_1, core_value_2, core_value_3')
        .eq('id', userId)
        .single();

      if (error) throw error;
      if (data) {
        const values = [
          data.core_value_1,
          data.core_value_2,
          data.core_value_3
        ].filter(Boolean);
        setCoreValues(values);
      }
    } catch (error) {
      console.error('Error loading core values:', error);
    }
  };

  const loadGoals = async () => {
    try {
      setIsLoading(true);
      const fetchedGoals = await fetchGoalsWithLadders(userId);
      setGoals(fetchedGoals);

      const counts: Record<string, number> = {};
      const recency: Record<string, 'green' | 'yellow' | 'red' | null> = {};

      for (const goal of fetchedGoals) {
        const { data: ladders } = await supabase
          .from('ladders')
          .select('id', { count: 'exact' })
          .eq('goal_id', goal.id);
        counts[goal.id] = ladders?.length || 0;

        const { data: recentStep } = await supabase
          .from('ladder_steps')
          .select('completed_at')
          .in('ladder_id', ladders?.map((l: any) => l.id) || [])
          .not('completed_at', 'is', null)
          .order('completed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const lastActivityAt = recentStep?.completed_at || goal.created_at;
        recency[goal.id] = calculateRecency(lastActivityAt);
      }

      setLadderCounts(counts);
      setGoalRecency(recency);

      if (fetchedGoals.length > 0 && !selectedGoalId) {
        const activeGoal = fetchedGoals.find((g) => g.status === 'active') || fetchedGoals[0];
        setSelectedGoalId(activeGoal.id);
        await loadGoalData(activeGoal.id);
      } else if (selectedGoalId) {
        await loadGoalData(selectedGoalId);
      }
    } catch (error) {
      console.error('Error loading goals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadGoalData = async (goalId: string) => {
    try {
      const [path, ladders] = await Promise.all([
        getMostActiveLadderPath(goalId),
        fetchLaddersForGoal(goalId),
      ]);
      setMostActivePath(path);
      setAllLadders(ladders);
    } catch (error) {
      console.error('Error loading goal data:', error);
    }
  };

  useEffect(() => {
    loadCoreValues();
    loadGoals();
  }, [userId]);

  const handleStepCompleted = () => {
    if (selectedGoalId) {
      loadGoalData(selectedGoalId);
    }
  };

  const handleLadderUpdated = () => {
    if (selectedGoalId) {
      loadGoalData(selectedGoalId);
    }
  };

  const startEditingGoal = (goal: Goal) => {
    setEditingGoalId(goal.id);
    setEditedName(goal.name);
    setEditedAlignment(goal.alignment || '');
    setEditedOutcome(goal.final_outcome);
  };

  const cancelEditingGoal = () => {
    setEditingGoalId(null);
    setEditedName('');
    setEditedAlignment('');
    setEditedOutcome('');
  };

  const saveGoalEdit = async () => {
    if (!editingGoalId || !editedName.trim() || !editedAlignment.trim() || !editedOutcome.trim()) return;

    try {
      const { error } = await supabase
        .from('goals')
        .update({
          name: editedName.trim(),
          alignment: editedAlignment.trim(),
          final_outcome: editedOutcome.trim(),
        })
        .eq('id', editingGoalId);

      if (error) throw error;

      setGoals(prev =>
        prev.map(g =>
          g.id === editingGoalId
            ? { ...g, name: editedName.trim(), alignment: editedAlignment.trim(), final_outcome: editedOutcome.trim() }
            : g
        )
      );

      cancelEditingGoal();
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const handleDragStart = (e: React.DragEvent, goalId: string) => {
    setDraggedGoalId(goalId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, goalId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedGoalId !== goalId) {
      setDragOverGoalId(goalId);
    }
  };

  const handleDragLeave = () => {
    setDragOverGoalId(null);
  };

  const handleDrop = async (e: React.DragEvent, targetGoalId: string) => {
    e.preventDefault();

    if (!draggedGoalId || draggedGoalId === targetGoalId) {
      setDraggedGoalId(null);
      setDragOverGoalId(null);
      return;
    }

    const draggedIndex = goals.findIndex(g => g.id === draggedGoalId);
    const targetIndex = goals.findIndex(g => g.id === targetGoalId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedGoalId(null);
      setDragOverGoalId(null);
      return;
    }

    const newGoals = [...goals];
    const [draggedGoal] = newGoals.splice(draggedIndex, 1);
    newGoals.splice(targetIndex, 0, draggedGoal);

    const updates = newGoals.map((goal, index) => ({
      id: goal.id,
      position: index,
    }));

    setGoals(newGoals.map((goal, index) => ({ ...goal, position: index })));

    try {
      for (const update of updates) {
        const { error } = await supabase
          .from('goals')
          .update({ position: update.position })
          .eq('id', update.id);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error updating goal positions:', error);
      loadGoals();
    }

    setDraggedGoalId(null);
    setDragOverGoalId(null);
  };

  const handleDragEnd = () => {
    setDraggedGoalId(null);
    setDragOverGoalId(null);
  };

  const handleTouchStart = (e: React.TouchEvent, goalId: string) => {
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
    setTouchDraggedGoalId(goalId);
    setDraggedGoalId(goalId);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchDraggedGoalId || !touchStartY) return;

    e.preventDefault();

    const touch = e.touches[0];
    setTouchCurrentY(touch.clientY);

    const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
    const goalElement = elements.find(el => el.hasAttribute('data-goal-id'));

    if (goalElement) {
      const hoveredGoalId = goalElement.getAttribute('data-goal-id');
      if (hoveredGoalId && hoveredGoalId !== touchDraggedGoalId) {
        setDragOverGoalId(hoveredGoalId);
      }
    }
  };

  const handleTouchEnd = async () => {
    if (!touchDraggedGoalId || !dragOverGoalId || touchDraggedGoalId === dragOverGoalId) {
      setTouchStartY(null);
      setTouchCurrentY(null);
      setTouchDraggedGoalId(null);
      setDraggedGoalId(null);
      setDragOverGoalId(null);
      return;
    }

    const draggedIndex = goals.findIndex(g => g.id === touchDraggedGoalId);
    const targetIndex = goals.findIndex(g => g.id === dragOverGoalId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setTouchStartY(null);
      setTouchCurrentY(null);
      setTouchDraggedGoalId(null);
      setDraggedGoalId(null);
      setDragOverGoalId(null);
      return;
    }

    const newGoals = [...goals];
    const [draggedGoal] = newGoals.splice(draggedIndex, 1);
    newGoals.splice(targetIndex, 0, draggedGoal);

    const updates = newGoals.map((goal, index) => ({
      id: goal.id,
      position: index,
    }));

    setGoals(newGoals.map((goal, index) => ({ ...goal, position: index })));

    try {
      for (const update of updates) {
        const { error } = await supabase
          .from('goals')
          .update({ position: update.position })
          .eq('id', update.id);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error updating goal positions:', error);
      loadGoals();
    }

    setTouchStartY(null);
    setTouchCurrentY(null);
    setTouchDraggedGoalId(null);
    setDraggedGoalId(null);
    setDragOverGoalId(null);
  };

  const handleTogglePriority = async (goalId: string, currentPriority: boolean) => {
    try {
      const success = await toggleGoalPriority(userId, goalId, !currentPriority);
      if (success) {
        await loadGoals();
      } else {
        alert('You can only have up to 3 priority goals.');
      }
    } catch (error) {
      console.error('Error toggling priority:', error);
      alert('Failed to update priority. Please try again.');
    }
  };

  return (
    <SectionAccordion
      title="Planner"
      icon={<Target className="w-5 h-5" />}
      open={isOpen}
      toggle={onToggle}
      sectionKey={sectionKey}
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : goals.length === 0 ? (
          <div className="text-center py-8">
            <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No action ladders yet</p>
            <Button onClick={() => navigate('/create-plan')}>
              <Plus className="w-4 h-4" />
              Create Your First Goal
            </Button>
          </div>
        ) : (
          <>
            {goals.map((goal) => (
              <div
                key={goal.id}
                data-goal-id={goal.id}
                draggable={editingGoalId !== goal.id}
                onDragStart={(e) => handleDragStart(e, goal.id)}
                onDragOver={(e) => handleDragOver(e, goal.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, goal.id)}
                onDragEnd={handleDragEnd}
                className={`border rounded-lg shadow-sm transition-all ${
                  goal.is_priority ? 'bg-green-50' : 'bg-white'
                } ${
                  selectedGoalId === goal.id && activeGoalExpanded
                    ? 'border-blue-400 ring-2 ring-blue-100'
                    : 'border-gray-200'
                } ${
                  draggedGoalId === goal.id ? 'opacity-50' : ''
                } ${
                  dragOverGoalId === goal.id ? 'border-blue-500 ring-2 ring-blue-200' : ''
                }`}
              >
                {editingGoalId === goal.id ? (
                  <div className="p-4 space-y-3">
                    <div className="flex items-center gap-3 mb-3">
                      <Target className="w-5 h-5 text-blue-600" />
                      <h4 className="text-sm font-medium text-gray-700">Edit Goal</h4>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Goal Name
                      </label>
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter goal name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Alignment
                      </label>
                      <textarea
                        value={editedAlignment}
                        onChange={(e) => setEditedAlignment(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={`How this goal aligns with ${formatCoreValuesText(coreValues)}.`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Declaration
                      </label>
                      <textarea
                        value={editedOutcome}
                        onChange={(e) => setEditedOutcome(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Declare the final outcome"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={cancelEditingGoal}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <button
                        onClick={saveGoalEdit}
                        disabled={!editedName.trim() || !editedAlignment.trim() || !editedOutcome.trim()}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-blue-50"
                    onClick={() => {
                      if (selectedGoalId === goal.id) {
                        setActiveGoalExpanded(!activeGoalExpanded);
                      } else {
                        setSelectedGoalId(goal.id);
                        loadGoalData(goal.id);
                        setActiveGoalExpanded(false);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div
                        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-2 -m-2"
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          if (editingGoalId !== goal.id) {
                            handleTouchStart(e, goal.id);
                          }
                        }}
                        onTouchMove={(e) => {
                          e.stopPropagation();
                          handleTouchMove(e);
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          handleTouchEnd();
                        }}
                        title="Drag to reorder"
                      >
                        <GripVertical className="w-5 h-5" />
                      </div>
                      {selectedGoalId === goal.id && activeGoalExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                      <div className="relative">
                        <Target className="w-5 h-5 text-blue-600" />
                        {goalRecency[goal.id] && (
                          <div
                            className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-white ${
                              goalRecency[goal.id] === 'green'
                                ? 'bg-green-500'
                                : goalRecency[goal.id] === 'yellow'
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {goal.name}
                          </h3>
                          {ladderCounts[goal.id] === 0 && (
                            <span className="text-xs font-medium px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                              No plan yet
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {goal.final_outcome}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTogglePriority(goal.id, goal.is_priority || false);
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                          goal.is_priority
                            ? 'text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50'
                            : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50'
                        }`}
                        title={goal.is_priority ? 'Remove from priorities' : 'Mark as priority'}
                      >
                        <Star className={`w-4 h-4 ${goal.is_priority ? 'fill-current' : ''}`} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditingGoal(goal);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit goal"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {selectedGoalId === goal.id && activeGoalExpanded && (
                  <div className="border-t border-gray-200 p-4 space-y-4">
                    {allLadders.length === 0 && !mostActivePath ? (
                      <div className="text-center py-8">
                        <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">No ladder created for this goal yet</p>
                        <Button onClick={() => navigate(`/create-ladder/${selectedGoalId}`)}>
                          <Plus className="w-4 h-4 mr-1" />
                          Create a Ladder
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div
                          className={`border rounded-lg bg-white transition-all ${
                            workingViewOpen
                              ? 'border-green-400 ring-2 ring-green-100'
                              : 'border-gray-200'
                          }`}
                        >
                          <div
                            className="flex items-center justify-between p-3 cursor-pointer hover:bg-blue-50"
                            onClick={() => {
                              setWorkingViewOpen(!workingViewOpen);
                              if (!workingViewOpen) {
                                setActiveView('working');
                                setFullViewOpen(false);
                              }
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {workingViewOpen ? (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                              <h4 className="font-medium text-gray-900">
                                Active Ladder
                              </h4>
                            </div>
                          </div>
                          {workingViewOpen && mostActivePath && (
                            <div className="border-t border-gray-200 p-4">
                              <MostActiveLadder
                                ladderPath={mostActivePath}
                                onStepCompleted={handleStepCompleted}
                                onEditClick={() => {
                                  setActiveView('full');
                                  setFullViewOpen(true);
                                  setWorkingViewOpen(false);
                                }}
                              />
                            </div>
                          )}
                        </div>

                        <div
                          className={`border rounded-lg bg-white transition-all ${
                            fullViewOpen
                              ? 'border-amber-400 ring-2 ring-amber-100'
                              : 'border-gray-200'
                          }`}
                        >
                          <div
                            className="flex items-center justify-between p-3 cursor-pointer hover:bg-blue-50"
                            onClick={() => {
                              setFullViewOpen(!fullViewOpen);
                              if (!fullViewOpen) {
                                setActiveView('full');
                                setWorkingViewOpen(false);
                              }
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {fullViewOpen ? (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                              <h4 className="font-medium text-gray-900">Full Ladder</h4>
                            </div>
                          </div>
                          {fullViewOpen && allLadders.length > 0 && (
                            <div className="border-t border-gray-200 p-4">
                              <FullLadderView
                                ladders={allLadders}
                                onLadderUpdated={handleLadderUpdated}
                              />
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}

            <Button
              variant="outline"
              onClick={() => navigate('/create-plan')}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Goal
            </Button>
          </>
        )}
      </div>
    </SectionAccordion>
  );
}
